import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function AboutPage() {
  const { t } = useTranslation();

  const breadcrumbItems = [
    { label: t('breadcrumb.home'), to: '/' },
    { label: t('nav.about') },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91在线吃瓜网',
    url: 'http://hnqxp01.cn/',
    description: '聚焦网红明星塌房现场、聊天记录泄露、反差视频的娱乐八卦资讯平台',
    foundingDate: '2023',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@hnqxp01.cn',
    },
  };

  const contactChannels = [
    {
      icon: 'ri-shake-hands-line',
      title: t('about.contact.cooperation'),
      desc: t('about.contact.cooperationDesc'),
    },
    {
      icon: 'ri-mail-send-line',
      title: t('about.contact.tip'),
      desc: t('about.contact.tipDesc'),
    },
    {
      icon: 'ri-advertisement-line',
      title: t('about.contact.advertise'),
      desc: t('about.contact.advertiseDesc'),
    },
    {
      icon: 'ri-alert-line',
      title: t('about.contact.complaint'),
      desc: t('about.contact.complaintDesc'),
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 | 91在线吃瓜网"
        description="91在线吃瓜网成立于2023年，是国内领先的娱乐八卦资讯平台。致力于为广大吃瓜群众提供最真实、最快速、最全面的娱乐爆料内容。"
        keywords="在线吃瓜, 91在线吃瓜网, 关于我们, 在线吃瓜爆料"
        canonicalUrl="http://hnqxp01.cn/about"
        schemaJson={orgSchema}
      />

      <section className="relative w-full min-h-[450px] md:min-h-[520px] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background-100/90 via-primary-100/30 to-accent-100/40"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-background-950"></div>

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-24">
          <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
            <div className="flex-1 w-full">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 mb-6">
                <span className="text-foreground-50 text-sm font-medium whitespace-nowrap">
                  {t('about.hero.badge')}
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-foreground-50 leading-tight">
                {t('about.hero.title.line1')}
                <br />
                {t('about.hero.title.line2')}
                <br />
                {t('about.hero.title.line3')}
              </h1>
            </div>

            <div className="flex-1 w-full flex flex-col justify-end">
              <div className="mb-6">
                <Breadcrumb items={breadcrumbItems} />
              </div>
              <p className="text-foreground-300 text-sm md:text-base leading-relaxed mb-8 max-w-lg">
                {t('about.hero.desc')}
              </p>
              <div className="flex items-center gap-6">
                <a
                  href="#brand"
                  className="px-8 py-3.5 bg-foreground-950 text-background-50 text-sm font-bold rounded-xl hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap"
                >
                  {t('about.hero.btn1')}
                </a>
                <a
                  href="#contact"
                  className="text-foreground-300 text-sm font-medium border-b border-foreground-500 hover:text-primary-400 hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('about.hero.btn2')}
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12 w-full">
            <div className="relative overflow-hidden rounded-2xl md:rounded-3xl">
              <div className="aspect-[21/9] w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20team%20collaboration%20scene%20with%20warm%20pink%20and%20golden%20lighting%2C%20modern%20office%20silhouettes%2C%20professional%20atmosphere%2C%20dramatic%20shadows%2C%20editorial%20photography%20style%2C%20artistic%20composition%2C%20film%20noir%20aesthetic&width=2100&height=900&seq=about-hero-banner&orientation=landscape"
                  alt="关于我们 - 91在线吃瓜网"
                  title="91在线吃瓜网 - 品牌介绍"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 via-transparent to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      <section id="brand" className="w-full py-16 md:py-20 bg-background-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            <div className="lg:w-2/5">
              <div className="inline-flex items-center px-4 py-2 rounded-full border border-foreground-300/40 bg-background-100 mb-6">
                <span className="text-foreground-700 text-xs font-medium whitespace-nowrap">
                  {t('about.brand.badge')}
                </span>
              </div>
              <div className="relative overflow-hidden rounded-3xl">
                <div className="aspect-[4/5] w-full">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20media%20certification%20and%20press%20credentials%20displayed%20on%20dark%20desk%2C%20warm%20ambient%20lighting%2C%20journalism%20awards%20and%20certificates%2C%20editorial%20photography%20style%2C%20cinematic%20composition%2C%20prestigious%20atmosphere%2C%20dark%20mood&width=800&height=1000&seq=about-brand-image&orientation=portrait"
                    alt="品牌信誉 - 91在线吃瓜网"
                    title="91在线吃瓜网 - 品牌信誉与资质"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="lg:w-3/5">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-foreground-950 leading-tight mb-6">
                {t('about.brand.title')}
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
                {t('about.brand.desc')}
              </p>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
                我们的团队由资深娱乐记者、互联网行业从业者和法律专业人士组成，确保每一条爆料的真实性、准确性和合法性。平台采用多重审核机制，对信息来源进行严格交叉验证，杜绝虚假信息和恶意造谣内容。
              </p>
              <button className="px-8 py-3.5 bg-foreground-950 text-background-50 text-sm font-bold rounded-xl hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap">
                {t('about.brand.btn')}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-20 bg-background-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            <div className="lg:w-3/5">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-2 h-2 rounded-full bg-foreground-950"></div>
                <span className="text-foreground-700 text-sm font-bold uppercase tracking-wider">
                  {t('about.testimonial.badge')}
                </span>
              </div>
              <p className="text-2xl md:text-3xl lg:text-4xl font-serif text-foreground-900 leading-relaxed mb-8">
                &ldquo;在这里吃到最真的瓜，每一个爆料都经得起推敲，比那些捕风捉影的营销号强太多了。&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <span className="text-foreground-800 text-sm font-bold">
                  资深吃瓜群众
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                  <i className="ri-star-fill"></i>
                  已验证用户
                </span>
              </div>
            </div>

            <div className="lg:w-2/5">
              <div className="relative">
                <div className="aspect-[3/4] w-full rounded-3xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Black%20and%20white%20portrait%20of%20a%20person%20with%20confident%20expression%2C%20looking%20slightly%20upward%2C%20dramatic%20studio%20lighting%2C%20clean%20white%20background%2C%20high%20contrast%2C%20editorial%20style%2C%20cinematic%20composition%2C%20professional%20headshot&width=600&height=800&seq=about-testimonial-portrait&orientation=portrait"
                    alt="用户口碑"
                    title="91在线吃瓜网 - 用户评价"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="w-full py-16 md:py-20 bg-background-950">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex flex-col items-center text-center mb-14">
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-foreground-300/30 bg-transparent mb-6">
              <span className="text-foreground-200 text-xs font-medium whitespace-nowrap">
                {t('about.contact.badge')}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-foreground-50 mb-4">
              {t('about.contact.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {contactChannels.map((ch) => (
              <div
                key={ch.title}
                className="group p-6 rounded-2xl bg-background-800/40 hover:bg-primary-500/10 border border-background-700/30 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-700/50 text-foreground-300 group-hover:text-primary-400 group-hover:bg-primary-500/15 transition-all mb-5">
                  <i className={`${ch.icon} text-2xl`}></i>
                </div>
                <h3 className="text-foreground-50 text-lg font-bold mb-2 group-hover:text-primary-400 transition-colors">
                  {ch.title}
                </h3>
                <p className="text-foreground-500 text-sm leading-relaxed mb-4">
                  {ch.desc}
                </p>
                <div className="flex items-center gap-2 text-primary-400 text-sm font-medium">
                  <span>了解更多</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-20 bg-background-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="relative overflow-hidden rounded-2xl md:rounded-3xl">
            <div className="aspect-[21/9] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20customer%20service%20concept%20with%20warm%20golden%20and%20pink%20lighting%2C%20communication%20and%20connection%20theme%2C%20abstract%20modern%20office%2C%20dramatic%20shadows%2C%20editorial%20photography%20style%2C%20artistic%20composition%2C%20film%20grain&width=2100&height=900&seq=about-cta-contact&orientation=landscape"
                alt="联系我们 - 91在线吃瓜网"
                title="加入吃瓜前线 - 91在线吃瓜网"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-background-950/80 to-background-950/20"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <p className="text-2xl md:text-4xl lg:text-5xl font-black text-foreground-50 mb-3 leading-tight">
                {t('about.cta.line1')}
              </p>
              <p className="text-foreground-300 text-sm md:text-base mb-8">
                <span className="italic font-light text-foreground-400">{t('about.cta.line2')}</span>
                {' · '}
                {t('about.cta.line3')}
              </p>
              <button className="px-10 py-4 bg-foreground-950 text-background-50 text-sm font-bold rounded-full hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap">
                {t('about.cta.btn')}
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}